import router from "@/router"
import { createApp } from "vue"
import App from "./App.vue"
import "@css/common.css"
import "@css/ec/ellipsis.css"
import "@css/flex.css"
import "@css/responsive.css"
import "@css/display-helper.css"
import "@css/card.css"
import { createPinia } from "pinia"
import OrganizationBreadcrumb from "@/organization/basic/components/OrganizationBreadcrumb.vue"
import EcIcon from "@/components/EcIcon.vue"
import EcColumn from "@/components/column/EcColumn.vue"
import EcFormBox from "@/components/EcFormBox.vue"
import EcFormItem from "@/components/EcFormItem.vue"
import EcEditableLabel from "@/components/EcEditableLabel.vue"
import EcButton from "@/components/EcButton.vue"
import EcButtonGroup from "@/components/EcButtonGroup.vue"
import EcSearchFilter from "@/components/EcSearchFilter.vue"
import EcSearchInput from "@/components/EcSearchInput.vue"
import EcTable from "@/components/EcTable.vue"
import EcHighlightLabel from "@/components/EcHighlightLabel.vue"
import EcPieChart from "@/components/charts/EcPieChart.vue"
import EcBarChart from "@/components/charts/EcBarChart.vue"
import EcEllipsis from "@/components/EcEllipsis.vue"
import EcNumberInput from "@/components/EcNumberInput.vue"
import EcUploadInput from "@/components/EcUploadInput.vue"
import EcUploadButton from "@/components/EcUploadButton.vue"
import EcTypeLabel from "@/components/EcTypeLabel.vue"
import EcSegment from "@/components/EcSegment.vue"
import EcDivider from "@/components/EcDivider.vue"
import EcAddressSelect from "@/components/EcAddressSelect.vue"
import EcAddressCascader from "@/components/EcAddressCascader.vue"
import EcHint from "@/components/EcHint.vue"
import EcNumberField from "@/components/EcNumberField.vue"
import EcImage from "@/components/EcImage.vue"
import EcList from "@/components/EcList.vue"
import EcTextField from "@/components/EcTextField.vue"
import EcSelect from "@/components/EcSelect.vue"
import EcFieldBox from "@/components/EcFieldBox.vue"
import EcField from "@/components/EcField.vue"
import EcInput from "@/components/EcInput.vue"
import EcDatePicker from "@/components/EcDatePicker.vue"
import EcDateRangePicker from "@/components/EcDateRangePicker.vue"
import EcFlex from "@/components/EcFlex.vue"
import EcCard from "./components/EcCard.vue"
import EcPage from "./components/EcPage.vue"
import EcPageTitle from "./components/EcPageTitle.vue"
import EcPanel from "./components/EcPanel.vue"
import { configureEcPlayer } from "@polarisle/ec-player-vue"
import "@polarisle/ec-player-vue/style.css"

import dayjs from "dayjs"
import updateLocale from "dayjs/plugin/updateLocale"
import "dayjs/locale/zh-cn"
dayjs.extend(updateLocale)
dayjs.locale("zh-cn")
dayjs.updateLocale("zh-cn", {
  weekStarts: 1,
})

configureEcPlayer({
  wasm: {
    simd: "/lib/ecplayer/decoder-simd.js",
    nonSimd: "/lib/ecplayer/decoder.js",
  },
})

import * as echarts from "echarts/core"
import { TooltipComponent, LegendComponent, TitleComponent, GridComponent, DataZoomComponent } from "echarts/components"
import { PieChart, BarChart, LineChart } from "echarts/charts"
import { LabelLayout } from "echarts/features"
import { CanvasRenderer } from "echarts/renderers"

echarts.use([TooltipComponent, LegendComponent, TitleComponent, GridComponent, DataZoomComponent, PieChart, BarChart, LineChart, CanvasRenderer, LabelLayout])

import {
  // create naive ui
  create,
  // component
  NButton,
  NSpace,
  NLayout,
  NLayoutContent,
  NLayoutFooter,
  NLayoutHeader,
  NLayoutSider,
  NMenu,
  NTabs,
  NTabPane,
  NList,
  NListItem,
  NAvatar,
  NDropdown,
  NGrid,
  NGridItem,
  NIcon,
  NText,
  NH1,
  NH2,
  NH3,
  NH4,
  NH5,
  NH6,
  NSpin,
  NUl,
  NLi,
  NOl,
  NP,
  NForm,
  NFormItem,
  NResult,
  NCard,
  NInput,
  NSwitch,
  NRadio,
  NRadioButton,
  NRadioGroup,
  NInputNumber,
  NSelect,
  NModal,
  NCheckboxGroup,
  NCheckbox,
  NTreeSelect,
  NPopover,
  NConfigProvider,
  NDivider,
  NDrawer,
  NDrawerContent,
  NSteps,
  NStep,
  NDataTable,
  NTag,
  NTree,
  NProgress,
  NCarousel,
  NCarouselItem,
  NImage,
  NBreadcrumb,
  NBreadcrumbItem,
  NDatePicker,
  NTimePicker,
  NAffix,
  NSlider,
  NPagination,
  NPopselect,
  NTooltip,
  NPopconfirm,
  NMessageProvider,
  NDialogProvider,
  NSkeleton,
  NScrollbar,
  NTab,
  NInputGroup,
  NInputGroupLabel,
  NEllipsis,
  NNumberAnimation,
  NVirtualList,
  NCascader,
  NInfiniteScroll,
  NAlert,
  NAnchor,
  NAnchorLink,
  NEmpty,
  NTransfer,
  NEmpty,
} from "naive-ui"

const naive = create({
  components: [
    NButton,
    NSpace,
    NLayout,
    NLayoutContent,
    NLayoutFooter,
    NLayoutHeader,
    NLayoutSider,
    NMenu,
    NTabs,
    NTabPane,
    NList,
    NListItem,
    NAvatar,
    NDropdown,
    NGrid,
    NGridItem,
    NIcon,
    NText,
    NH1,
    NH2,
    NH3,
    NH4,
    NH5,
    NH6,
    NSpin,
    NUl,
    NLi,
    NOl,
    NP,
    NForm,
    NFormItem,
    NResult,
    NCard,
    NInput,
    NInputGroup,
    NSwitch,
    NRadio,
    NRadioButton,
    NRadioGroup,
    NInputNumber,
    NSelect,
    NModal,
    NCheckboxGroup,
    NCheckbox,
    NTreeSelect,
    NPopover,
    NConfigProvider,
    NDivider,
    NDrawer,
    NDrawerContent,
    NSteps,
    NStep,
    NDataTable,
    NTag,
    NTree,
    NProgress,
    NCarousel,
    NCarouselItem,
    NImage,
    NBreadcrumb,
    NBreadcrumbItem,
    NDatePicker,
    NTimePicker,
    NAffix,
    NSlider,
    NPagination,
    NPopselect,
    NTooltip,
    NPopconfirm,
    NMessageProvider,
    NDialogProvider,
    NSkeleton,
    NScrollbar,
    NTab,
    NInputGroupLabel,
    NEllipsis,
    NNumberAnimation,
    NVirtualList,
    NCascader,
    NInfiniteScroll,
    NAlert,
    NAnchor,
    NAnchorLink,
    NEmpty,
    NTransfer,
    NEmpty,
  ],
})

const pinia = createPinia()
const app = createApp(App)

app.use(pinia)
app.use(router)
app.use(naive)

import vLoading, { injectLoadingStyle } from "@/directive/vLoading"
injectLoadingStyle()
app.directive("loading", vLoading)
app
  .component("OrganizationBreadcrumb", OrganizationBreadcrumb)
  .component("EcIcon", EcIcon)
  .component("EcColumn", EcColumn)
  .component("EcFormBox", EcFormBox)
  .component("EcFormItem", EcFormItem)
  .component("EcEditableLabel", EcEditableLabel)
  .component("EcButton", EcButton)
  .component("EcButtonGroup", EcButtonGroup)
  .component("EcSearchFilter", EcSearchFilter)
  .component("EcSearchInput", EcSearchInput)
  .component("EcTable", EcTable)
  .component("EcHighlightLabel", EcHighlightLabel)
  .component("EcPieChart", EcPieChart)
  .component("EcBarChart", EcBarChart)
  .component("EcEllipsis", EcEllipsis)
  .component("EcNumberInput", EcNumberInput)
  .component("EcUploadInput", EcUploadInput)
  .component("EcUploadButton", EcUploadButton)
  .component("EcTypeLabel", EcTypeLabel)
  .component("EcSegment", EcSegment)
  .component("EcDivider", EcDivider)
  .component("EcAddressSelect", EcAddressSelect)
  .component("EcAddressCascader", EcAddressCascader)
  .component("EcHint", EcHint)
  .component("EcNumberField", EcNumberField)
  .component("EcImage", EcImage)
  .component("EcList", EcList)
  .component("EcTextField", EcTextField)
  .component("EcFieldBox", EcFieldBox)
  .component("EcField", EcField)
  .component("EcInput", EcInput)
  .component("EcDatePicker", EcDatePicker)
  .component("EcDateRangePicker", EcDateRangePicker)
  .component("EcFlex", EcFlex)
  .component("EcCard", EcCard)
  .component("EcPage", EcPage)
  .component("EcPageTitle", EcPageTitle)
  .component("EcPanel", EcPanel)

app.mount("#app")
